import React from "react";
import { Link } from "react-router-dom";
import { X, Trash2, ShoppingBag } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";

const specRow = (label, values, formatter = (v) => v || "—") => (
  <div className="grid gap-0" style={{ gridTemplateColumns: `140px repeat(${values.length}, minmax(0, 1fr))` }}>
    <div className="py-3 px-4 text-[11px] uppercase tracking-[0.16em] text-[#7a7a7a] font-medium border-b border-[#e8e3d6]">
      {label}
    </div>
    {values.map((v, i) => (
      <div
        key={`${label}-${i}`}
        className="py-3 px-4 text-sm text-[#1B1B1B] border-b border-[#e8e3d6] font-medium"
        data-testid={`compare-cell-${label.toLowerCase().replace(/\s+/g, "-")}-${i}`}
      >
        {formatter(v)}
      </div>
    ))}
  </div>
);

const price = (usd) => (typeof usd === "number" && usd > 0 ? `$${usd.toLocaleString()}` : "—");

export default function ComparisonDrawer() {
  const {
    compareList,
    compareOpen,
    closeCompare,
    removeFromCompare,
    clearCompare,
    addToCart,
    COMPARE_MAX,
  } = useApp();

  if (!compareOpen) return null;

  const items = compareList.slice(0, COMPARE_MAX);
  const empty = items.length === 0;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={closeCompare}
      data-testid="compare-drawer-overlay"
    >
      <div
        className="w-full sm:max-w-6xl bg-[#F9F6F0] border-t sm:border border-[#d6d1c4] max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="compare-drawer"
      >
        <div className="flex items-center justify-between px-6 sm:px-8 py-5 border-b border-[#d6d1c4] bg-white">
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase text-[#C85A32] font-bold mb-1">
              Comparing {items.length} of {COMPARE_MAX}
            </div>
            <h2 className="font-serif text-2xl sm:text-3xl text-[#1B1B1B]">
              Side-by-side plan comparison
            </h2>
          </div>
          <div className="flex items-center gap-2">
            {!empty && (
              <button
                onClick={clearCompare}
                data-testid="compare-clear-all"
                className="text-xs uppercase tracking-[0.18em] text-[#7a7a7a] hover:text-[#1B1B1B] px-3 py-2"
              >
                Clear all
              </button>
            )}
            <button
              onClick={closeCompare}
              data-testid="compare-close"
              aria-label="Close"
              className="w-9 h-9 flex items-center justify-center hover:bg-[#F9F6F0] transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="overflow-auto flex-1">
          {empty ? (
            <div className="py-20 px-8 text-center" data-testid="compare-empty">
              <div className="text-[#7a7a7a] text-sm">
                No plans selected yet. Add up to {COMPARE_MAX} plans using the &ldquo;Compare&rdquo; toggle on each plan card.
              </div>
            </div>
          ) : (
            <>
              <div className="grid gap-0 bg-white" style={{ gridTemplateColumns: `140px repeat(${items.length}, minmax(0, 1fr))` }}>
                <div className="py-4 px-4 border-b border-[#e8e3d6]"></div>
                {items.map((p) => (
                  <div
                    key={p.id}
                    className="py-4 px-4 border-b border-[#e8e3d6] relative"
                    data-testid={`compare-column-${p.id}`}
                  >
                    <button
                      onClick={() => removeFromCompare(p.id)}
                      data-testid={`compare-remove-${p.id}`}
                      aria-label={`Remove ${p.title}`}
                      className="absolute top-2 right-2 w-7 h-7 flex items-center justify-center bg-white border border-[#d6d1c4] hover:bg-[#F9F6F0] rounded-full text-[#7a7a7a]"
                    >
                      <Trash2 size={14} />
                    </button>
                    <Link to={`/plans/${p.slug || p.id}`} className="block group">
                      <div className="aspect-[4/3] bg-[#f0eadd] mb-3 overflow-hidden">
                        {p.image_urls?.[0] || p.cover_image ? (
                          <img
                            src={p.image_urls?.[0] || p.cover_image}
                            alt={p.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-[#7a7a7a] text-xs">No image</div>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#C85A32] font-bold">{p.style || "—"}</div>
                      <div className="font-serif text-lg text-[#1B1B1B] mt-1 line-clamp-2 group-hover:text-[#C85A32] transition-colors">
                        {p.title}
                      </div>
                    </Link>
                  </div>
                ))}
              </div>

              {specRow("Price", items.map((p) => p.price_usd), price)}
              {specRow("Bedrooms", items.map((p) => p.bedrooms))}
              {specRow("Bathrooms", items.map((p) => p.bathrooms))}
              {specRow("Square feet", items.map((p) => p.sqft), (v) => (v ? `${v.toLocaleString()} sq ft` : "—"))}
              {specRow("Stories", items.map((p) => p.stories))}
              {specRow("Garage bays", items.map((p) => p.garage_bays ?? p.garage))}
              {specRow("Style", items.map((p) => p.style))}

              <div className="grid gap-0 bg-white" style={{ gridTemplateColumns: `140px repeat(${items.length}, minmax(0, 1fr))` }}>
                <div className="py-4 px-4"></div>
                {items.map((p) => (
                  <div key={p.id} className="py-4 px-4 flex flex-col gap-2">
                    <Button
                      onClick={() => {
                        addToCart(p);
                        removeFromCompare(p.id);
                      }}
                      data-testid={`compare-buy-${p.id}`}
                      className="w-full bg-[#2C4C3B] hover:bg-[#1e3628] text-white rounded-none text-xs uppercase tracking-[0.16em] py-3 h-auto"
                    >
                      <ShoppingBag size={14} className="mr-2" /> Add to cart
                    </Button>
                    <Link
                      to={`/plans/${p.slug || p.id}`}
                      onClick={closeCompare}
                      data-testid={`compare-view-${p.id}`}
                      className="text-center text-[11px] uppercase tracking-[0.16em] text-[#2C4C3B] hover:text-[#C85A32] py-2 border border-[#2C4C3B]"
                    >
                      View details
                    </Link>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
